import { Trans } from "@lingui/react/macro";
import { PanelLeftIcon } from "lucide-react";
import * as React from "react";

import { useIsMobile } from "@/utils/hooks/use-mobile";
import { cn } from "@/utils/shadcn-ui";

import { Button } from "./button";
import { Sheet, SheetContent, SheetDescription, SheetTitle } from "./sheet";

const SIDEBAR_COOKIE_NAME = "sidebar_state";
// A year rather than shadcn's week: a preference that silently expires puts
// someone who expanded the sidebar back on the collapsed default.
const SIDEBAR_COOKIE_MAX_AGE = 60 * 60 * 24 * 365;
// Rail + level-2 panel: 3.5rem + 13.5rem. The panel's own width is set in
// app-sidebar.tsx and the two must stay in step — the panel is positioned from
// the rail's edge, so any slack or overflow shows at the sidebar's right edge.
// This is the *expanded-panel* total; with the panel collapsed to its own icon
// strip app-sidebar.tsx passes the narrower total through `Sidebar`'s `width`.
// Sized to the longest panel label across the shipped locales rather than to
// English alone: "Monitoring management" needs 148px at the current row's
// weight, its Italian "Gestione del monitoraggio" 165px — see the panel's own
// note for how the row spends the rest.
const SIDEBAR_WIDTH = "17rem";
// Matches SIDEBAR_WIDTH rather than carrying a width of its own: the drawer
// holds the same two-level chrome, whose rail + panel total exactly that. Any
// wider and the sheet shows a strip of empty surface past the panel's edge.
const SIDEBAR_WIDTH_MOBILE = SIDEBAR_WIDTH;
// The icon rail. 56px per the design handoff, and the chain below is built to
// centre a collapsed icon in exactly that: list padding 8 + row 40 + 8, with the
// row's own `px-2.5` (10) + 20px icon box putting the icon at 18–38, centred on
// 28 = 56/2. Change this and you must re-derive that chain, not just the number.
const SIDEBAR_WIDTH_ICON = "3.5rem";

/** The persisted open state, for `SidebarProvider`'s `defaultOpen`. Collapsed
 * until the user opens it: the rail is meant to be used by its icons, and
 * starting there is how they get learned. */
function getSidebarDefaultOpen() {
  return document.cookie.split("; ").includes(`${SIDEBAR_COOKIE_NAME}=true`);
}

type SidebarContextProps = {
  state: "expanded" | "collapsed";
  open: boolean;
  setOpen: (open: boolean) => void;
  openMobile: boolean;
  setOpenMobile: (open: boolean) => void;
  isMobile: boolean;
  toggleSidebar: () => void;
};

const SidebarContext = React.createContext<SidebarContextProps | null>(null);

function useSidebar() {
  const context = React.useContext(SidebarContext);
  if (!context) {
    throw new Error("useSidebar must be used within a SidebarProvider.");
  }
  return context;
}

function SidebarProvider({
  defaultOpen = true,
  open: openProp,
  onOpenChange: setOpenProp,
  className,
  style,
  children,
  ...props
}: React.ComponentProps<"div"> & {
  defaultOpen?: boolean;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
}) {
  const isMobile = useIsMobile();
  const [openMobile, setOpenMobile] = React.useState(false);

  // Internal state, used unless the consumer controls `open` itself.
  const [_open, _setOpen] = React.useState(defaultOpen);
  const open = openProp ?? _open;
  const setOpen = React.useCallback(
    (value: boolean | ((value: boolean) => boolean)) => {
      const openState = typeof value === "function" ? value(open) : value;
      if (setOpenProp) {
        setOpenProp(openState);
      } else {
        _setOpen(openState);
      }

      // Persist across reloads.
      document.cookie = `${SIDEBAR_COOKIE_NAME}=${openState}; path=/; max-age=${SIDEBAR_COOKIE_MAX_AGE}`;
    },
    [setOpenProp, open],
  );

  const toggleSidebar = React.useCallback(() => {
    return isMobile ? setOpenMobile((open) => !open) : setOpen((open) => !open);
  }, [isMobile, setOpen, setOpenMobile]);

  // No ⌘B binding here, unlike shadcn's: it is bound in app-sidebar.tsx,
  // beside the footer control and the double-click that do the same, through
  // the `useShortcut` helper the app's other shortcuts use — which can skip it
  // while typing, where ⌘B is bold in the rich-text editors.

  // Exposed as a data attribute so the rest of the app can style against it.
  const state = open ? "expanded" : "collapsed";

  const contextValue = React.useMemo<SidebarContextProps>(
    () => ({
      state,
      open,
      setOpen,
      isMobile,
      openMobile,
      setOpenMobile,
      toggleSidebar,
    }),
    [state, open, setOpen, isMobile, openMobile, setOpenMobile, toggleSidebar],
  );

  return (
    <SidebarContext.Provider value={contextValue}>
      <div
        data-slot="sidebar-wrapper"
        style={
          {
            "--sidebar-width": SIDEBAR_WIDTH,
            "--sidebar-width-icon": SIDEBAR_WIDTH_ICON,
            ...style,
          } as React.CSSProperties
        }
        className={cn(
          "group/sidebar-wrapper has-data-[variant=inset]:bg-sidebar flex min-h-svh w-full",
          className,
        )}
        {...props}
      >
        {children}
      </div>
    </SidebarContext.Provider>
  );
}

function Sidebar({
  side = "left",
  variant = "sidebar",
  collapsible = "offcanvas",
  width,
  className,
  children,
  ...props
}: React.ComponentProps<"div"> & {
  side?: "left" | "right";
  variant?: "sidebar" | "floating" | "inset";
  collapsible?: "offcanvas" | "icon" | "none";
  /**
   * Pins the expanded *and* collapsed widths to this value, so the sidebar's
   * total is driven by its contents rather than by its own collapsed state.
   * The two-level nav needs it: while a level-2 panel is open the width is
   * rail + panel, expanded or collapsed, not the rail's own. Desktop only —
   * the mobile drawer keeps the sheet's fixed width.
   */
  width?: string;
}) {
  const { isMobile, state, openMobile, setOpenMobile } = useSidebar();

  if (collapsible === "none") {
    return (
      <div
        data-slot="sidebar"
        className={cn(
          "bg-sidebar text-sidebar-foreground flex h-full w-(--sidebar-width) flex-col",
          className,
        )}
        {...props}
      >
        {children}
      </div>
    );
  }

  if (isMobile) {
    return (
      <Sheet open={openMobile} onOpenChange={setOpenMobile} {...props}>
        <SheetContent
          data-sidebar="sidebar"
          data-slot="sidebar"
          data-mobile="true"
          side={side}
          showClose={false}
          className="bg-sidebar text-sidebar-foreground w-(--sidebar-width) max-w-[100vw] gap-0 p-0"
          style={
            { "--sidebar-width": SIDEBAR_WIDTH_MOBILE } as React.CSSProperties
          }
        >
          <SheetTitle className="sr-only">
            <Trans>Navigation</Trans>
          </SheetTitle>
          <SheetDescription className="sr-only">
            <Trans>Displays the navigation menu.</Trans>
          </SheetDescription>
          {/* `relative` + `overflow-hidden`, as on the desktop sidebar-inner
              below: the level-2 panel is positioned against this and parks
              past its right edge when hidden, so without the clip its slide
              ran out over the page. */}
          <div className="relative flex h-full w-full flex-col overflow-hidden">
            {children}
          </div>
        </SheetContent>
      </Sheet>
    );
  }

  return (
    <div
      // Set on the root, not on the two width-bearing children: both
      // `sidebar-gap` and `sidebar-container` already read these vars, so
      // overriding them here needs no class changes and keeps the collapsed
      // branch working as written.
      style={
        width
          ? ({
              "--sidebar-width": width,
              "--sidebar-width-icon": width,
            } as React.CSSProperties)
          : undefined
      }
      className="group peer text-sidebar-foreground hidden nav:block"
      data-state={state}
      data-collapsible={state === "collapsed" ? collapsible : ""}
      data-variant={variant}
      data-side={side}
      data-slot="sidebar"
    >
      {/* Sizing spacer: the sidebar itself is fixed, this holds the gap open. */}
      <div
        data-slot="sidebar-gap"
        className={cn(
          // Same duration and curve as the level-2 panel and the label fade
          // (the handoff's one shared ease), so the whole sidebar moves as a
          // single motion rather than two that end at different times.
          "relative w-(--sidebar-width) bg-transparent transition-[width] duration-280 ease-[cubic-bezier(.4,0,.2,1)]",
          "group-data-[collapsible=offcanvas]:w-0",
          "group-data-[side=right]:rotate-180",
          variant === "floating" || variant === "inset"
            ? "group-data-[collapsible=icon]:w-[calc(var(--sidebar-width-icon)+(--spacing(4)))]"
            : "group-data-[collapsible=icon]:w-(--sidebar-width-icon)",
        )}
      />
      <div
        data-slot="sidebar-container"
        className={cn(
          // Starts below the app's fixed header (`--app-header-height`, set by
          // main-layout.tsx) instead of at the viewport top (shadcn's default
          // `inset-y-0`) — the header
          // is a separate top bar, not part of the sidebar, and this `fixed`
          // element positions against the viewport regardless of where it's
          // nested, so the offset has to be spelled out here rather than
          // solved by DOM nesting. `top`+`bottom` both set, no explicit
          // height, so it fills the space between them on its own.
          "fixed top-(--app-header-height) bottom-0 z-10 hidden w-(--sidebar-width) transition-[left,right,width] duration-280 ease-[cubic-bezier(.4,0,.2,1)] motion-reduce:transition-none nav:flex",
          side === "left"
            ? "left-0 group-data-[collapsible=offcanvas]:left-[calc(var(--sidebar-width)*-1)]"
            : "right-0 group-data-[collapsible=offcanvas]:right-[calc(var(--sidebar-width)*-1)]",
          // Adjust the padding for floating and inset variants.
          // The edge border is `--sidebar-edge`, not shadcn's unthemed generic
          // `--border`, so the rail's edge against the content can be tuned
          // apart from the header underline and the panel's edge.
          "border-sidebar-edge",
          variant === "floating" || variant === "inset"
            ? "p-2 group-data-[collapsible=icon]:w-[calc(var(--sidebar-width-icon)+(--spacing(4))+2px)]"
            : "group-data-[collapsible=icon]:w-(--sidebar-width-icon) group-data-[side=left]:border-r group-data-[side=right]:border-l",
          className,
        )}
        {...props}
      >
        <div
          data-sidebar="sidebar"
          data-slot="sidebar-inner"
          // `relative` + `overflow-hidden`: the second-level panel is positioned
          // against this and clipped by it, so collapsing the rail hides the
          // panel by narrowing its container rather than animating the panel.
          className="bg-sidebar group-data-[variant=floating]:border-sidebar-border relative flex h-full w-full flex-col overflow-hidden group-data-[variant=floating]:rounded-lg group-data-[variant=floating]:border group-data-[variant=floating]:shadow-sm"
        >
          {children}
        </div>
      </div>
    </div>
  );
}

function SidebarTrigger({
  className,
  onClick,
  children,
  ...props
}: React.ComponentProps<typeof Button>) {
  const { toggleSidebar } = useSidebar();

  return (
    <Button
      data-sidebar="trigger"
      data-slot="sidebar-trigger"
      variant="tertiary"
      size="md"
      iconOnly
      className={className}
      onClick={(event) => {
        onClick?.(event);
        toggleSidebar();
      }}
      {...props}
    >
      {children ?? <PanelLeftIcon />}
      <span className="sr-only">
        <Trans>Toggle sidebar</Trans>
      </span>
    </Button>
  );
}

// A layout wrapper, not a landmark — the page keeps its own <main>, so this
// deviates from shadcn's <main> to avoid nesting the two.
function SidebarInset({ className, ...props }: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="sidebar-inset"
      className={cn(
        // `min-w-0` (not in shadcn's original): without it the inset's
        // automatic minimum size lets wide content — data tables, mostly —
        // push it past the viewport instead of scrolling inside it.
        "bg-background relative flex w-full min-w-0 flex-1 flex-col",
        "nav:peer-data-[variant=inset]:m-2 nav:peer-data-[variant=inset]:ml-0 nav:peer-data-[variant=inset]:rounded-xl nav:peer-data-[variant=inset]:shadow-sm nav:peer-data-[variant=inset]:peer-data-[state=collapsed]:ml-2",
        className,
      )}
      {...props}
    />
  );
}

function SidebarFooter({ className, ...props }: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="sidebar-footer"
      data-sidebar="footer"
      className={cn("flex flex-col gap-2 p-2", className)}
      {...props}
    />
  );
}

export {
  getSidebarDefaultOpen,
  Sidebar,
  SIDEBAR_WIDTH,
  SidebarFooter,
  SidebarInset,
  SidebarProvider,
  SidebarTrigger,
  useSidebar,
};
